import Terraformer from 'terraformer-arcgis-parser';
import { MapDrawFuncParams, SketchView, GeocodeType, AddressType } from 'types';
import Util from 'utilities/helper.util';
import MapSettings from 'components/Map/settings';
import { zoomToFeature } from 'components/Map/helper';
import { addLayerToMap, findLayerById } from 'utilities/map-instance.util';
import { debugLog, debugWarn } from 'utilities/basemap-debug.util';

export const geocodeToAddress = (geocode: GeocodeType): AddressType => {
  const { formattedaddress, locality, region, postcode } = geocode;
  const breakpoint = formattedaddress.indexOf(`, ${locality}, ${region}`);
  const newAddress = formattedaddress.substring(0, breakpoint);
  const addressWithLevel = /^((suite|shop|unit)\s.+,\s)?Level\s.+,/i.exec(
    newAddress
  );
  const trimedAddress = Array.isArray(addressWithLevel)
    ? newAddress.replace(addressWithLevel[0], '')
    : newAddress;

  return {
    line1: Util.toTitleCase(trimedAddress),
    line2: '',
    country: 'AUS',
    locality: Util.toTitleCase(locality),
    postcode,
    state: region
  };
};

export const addGraphic = (
  { graphic, graphics, drawingLayer }: MapDrawFuncParams,
  tool: string,
  baseMapTheme: string
): any => {
  const { loadedSketchViewModel, loadedMapModules } = window;
  const { GeometryEngine, WebMercatorUtils, Graphic } = loadedMapModules;
  const maxAreaUnit = 'square-kilometers';
  const maxArea = 20;

  let drawedGraphic = graphics !== undefined ? graphics[0] : graphic;

  if (
    drawedGraphic === undefined ||
    (drawedGraphic?.geometry?.type !== 'polygon' &&
      drawedGraphic?.geometry?.type !== 'polyline')
  ) {
    throw new Error('Invalid type geometry.');
  }

  if (drawedGraphic.geometry.type === 'polyline') {
    const buffer = GeometryEngine.geodesicBuffer(
      drawedGraphic.geometry,
      10,
      'meters'
    );
    drawedGraphic = new Graphic({
      geometry: buffer,
      symbol:
        baseMapTheme === 'dark'
          ? MapSettings.polygonSymbolDarkCompleteDraw
          : MapSettings.polygonSymbol
    });
  }

  // Throw error if circle diameter less than 10m diameter
  if (drawedGraphic.geometry.extent.width < 10 && tool === 'circle') {
    clearGraphic(drawingLayer, loadedSketchViewModel);
    throw new Error('Circle diameter must be more than 10m.');
  }

  // Buffer small polygon to minimum of 10m for both extent width and height.
  // We already block circle, no need buffer
  // Polyline will always buffer to 10m upon drawing, no need
  if (tool === 'polygon') {
    if (
      drawedGraphic.geometry.extent.width < 10 ||
      drawedGraphic.geometry.extent.height < 10
    ) {
      const lowestMeasurement =
        drawedGraphic.geometry.extent.width <
        drawedGraphic.geometry.extent.height
          ? drawedGraphic.geometry.extent.width
          : drawedGraphic.geometry.extent.height;

      // Division of 2 to make the minimum width/height near to 10m
      // Trying not to bump too much which might end up distort the original intention of the dig site
      const buffer = GeometryEngine.geodesicBuffer(
        drawedGraphic.geometry,
        (10 - Math.floor(lowestMeasurement)) / 2,
        'meters'
      );
      if (!buffer) {
        //Getting null after buffer if both line are too close together even not Self-intersecting
        clearGraphic(drawingLayer, loadedSketchViewModel);
        throw new Error(
          'Self-intersecting polygon has been detected and is not supported.'
        );
      }
      drawedGraphic = new Graphic({
        geometry: buffer,
        symbol:
          baseMapTheme === 'dark'
            ? MapSettings.polygonSymbolDarkCompleteDraw
            : MapSettings.polygonSymbol
      });
    }
  }

  const geometry: __esri.Polygon = drawedGraphic.geometry;
  const geoGraphicGeometry = WebMercatorUtils.webMercatorToGeographic(geometry);
  const polygonInGeoJson = Terraformer.parse(geoGraphicGeometry);
  const geometryArea = GeometryEngine.geodesicArea(geometry, maxAreaUnit);

  // Ensure the graphic has the correct symbol based on theme
  drawedGraphic.symbol =
    baseMapTheme === 'dark'
      ? MapSettings.polygonSymbolDarkCompleteDraw
      : MapSettings.polygonSymbol;

  if (drawingLayer) {
    drawingLayer.removeAll();
    drawingLayer.add(drawedGraphic);
    drawingLayer.emit('change');
  }

  if (
    polygonInGeoJson.type === 'MultiPolygon' ||
    geometry.isSelfIntersecting ||
    geometry.rings.length > 1
  ) {
    clearGraphic(drawingLayer, loadedSketchViewModel);
    throw new Error(
      'Self-intersecting polygon has been detected and is not supported.'
    );
  }

  if (geometryArea > maxArea) {
    clearGraphic(drawingLayer, loadedSketchViewModel);
    const myError = new Error(
      `The maximum is ${maxArea} square kilometres but your area is ${geometryArea.toFixed(
        1
      )} km²`
    );
    myError.name = 'The shape is too large';
    throw myError;
  }

  const centroid = {
    latitude: geometry.centroid.latitude,
    longitude: geometry.centroid.longitude
  };

  return { polygonInGeoJson, centroid, geometryArea, geometry };
};

export const addClonedShapeToMap = (
  shape: GeoJSON.GeometryObject,
  zoomToShape: boolean = true,
  shapeArea: number = 0,
  baseMapTheme?: string
) => {
  const { loadedMapModules, loadedView } = window;
  debugLog('[addClonedShapeToMap] Called with:', {
    zoomToShape,
    shapeArea,
    hasLoadedMapModules: !!loadedMapModules,
    hasLoadedView: !!loadedView,
    shapeType: shape?.type,
    baseMapTheme,
    timestamp: new Date().toISOString()
  });

  if (loadedMapModules) {
    const { Graphic, WebMercatorUtils, GraphicsLayer } = loadedMapModules;
    // Use centralized utility to find layer
    let cloneShapeLayer: any = findLayerById('cloned-shape-layer');
    debugLog('[addClonedShapeToMap] Clone shape layer found:', {
      exists: !!cloneShapeLayer,
      graphicsCount: cloneShapeLayer?.graphics?.length || 0,
      timestamp: new Date().toISOString()
    });

    if (!cloneShapeLayer) {
      debugLog('[addClonedShapeToMap] Creating new cloned-shape-layer', {
        timestamp: new Date().toISOString()
      });
      cloneShapeLayer = new GraphicsLayer({ id: 'cloned-shape-layer' });
      addLayerToMap(cloneShapeLayer);
      debugLog('[addClonedShapeToMap] Created and added cloned-shape-layer', {
        timestamp: new Date().toISOString()
      });
    }

    const geometry: any = Terraformer.convert(shape);
    const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
      geometry
    );

    const symbolToUse =
      baseMapTheme === 'dark'
        ? MapSettings.polygonSymbolDarkCompleteDraw
        : MapSettings.polygonSymbol;

    const graphic = new Graphic({
      geometry: {
        ...webMercatorGeometry,
        type: 'polygon'
      },
      symbol: symbolToUse
    });

    // Clear existing graphics before adding new one to avoid duplicates
    // This is especially important when the function is called multiple times
    // (e.g., from useEffect and onMapReady)
    if (cloneShapeLayer.graphics.length > 0) {
      debugLog(
        '[addClonedShapeToMap] Clearing existing graphics before adding new one',
        {
          existingCount: cloneShapeLayer.graphics.length,
          timestamp: new Date().toISOString()
        }
      );
      cloneShapeLayer.removeAll();
    }

    debugLog('[addClonedShapeToMap] Adding graphic to layer', {
      timestamp: new Date().toISOString()
    });
    cloneShapeLayer.add(graphic);
    debugLog('[addClonedShapeToMap] Graphic added. Layer graphics count:', {
      count: cloneShapeLayer.graphics.length,
      timestamp: new Date().toISOString()
    });

    let unitToUse = 'kilometers';
    if (shapeArea <= 0.01) {
      unitToUse = 'meters';
    }

    if (zoomToShape) {
      debugLog('[addClonedShapeToMap] Zooming to shape extent:', {
        extent: graphic.geometry.extent,
        timestamp: new Date().toISOString()
      });
      zoomToFeature(graphic.geometry.extent, 1, false, unitToUse);
    } else if (loadedView && !loadedView.destroyed) {
      // Keep the user's current view after manual draw / preset return.
      // Restoring mapOriginalExtent here zoomed out to the initial Australia-wide
      // extent when the user had panned to their job site before drawing.
      debugLog('[addClonedShapeToMap] Keeping current map extent', {
        timestamp: new Date().toISOString()
      });
      window.mapOriginalExtent = loadedView.extent;
    } else {
      debugLog(
        '[addClonedShapeToMap] No zoom action - view not ready or destroyed',
        {
          timestamp: new Date().toISOString()
        }
      );
    }
  } else {
    debugWarn('[addClonedShapeToMap] loadedMapModules not available', {
      timestamp: new Date().toISOString()
    });
  }
};

export const removeClonedShapeLayer = () => {
  debugLog('[removeClonedShapeLayer] Called', {
    timestamp: new Date().toISOString()
  });
  const { loadedMapModules } = window;
  if (loadedMapModules) {
    // Use centralized utility to remove layer
    const cloneShapeLayer: any = findLayerById('cloned-shape-layer');
    debugLog('[removeClonedShapeLayer] Layer found:', {
      exists: !!cloneShapeLayer,
      graphicsCount: cloneShapeLayer?.graphics?.length || 0,
      timestamp: new Date().toISOString()
    });
    if (cloneShapeLayer) {
      const { loadedView } = window;
      if (loadedView) {
        debugLog('[removeClonedShapeLayer] Removing layer from map', {
          timestamp: new Date().toISOString()
        });
        loadedView.map.remove(cloneShapeLayer);
        debugLog('[removeClonedShapeLayer] Layer removed', {
          timestamp: new Date().toISOString()
        });
      } else {
        debugLog(
          '[removeClonedShapeLayer] No loadedView, cannot remove layer',
          {
            timestamp: new Date().toISOString()
          }
        );
      }
    } else {
      debugLog('[removeClonedShapeLayer] Layer not found, nothing to remove', {
        timestamp: new Date().toISOString()
      });
    }
  } else {
    debugLog('[removeClonedShapeLayer] loadedMapModules not available', {
      timestamp: new Date().toISOString()
    });
  }
};

export const getCentroidFromGeoJson = (
  geoJson: GeoJSON.GeometryObject
): { latitude: number; longitude: number } | null => {
  const { loadedMapModules } = window;
  if (!loadedMapModules) {
    return null;
  }

  const { Graphic, WebMercatorUtils } = loadedMapModules;
  const geometry: any = Terraformer.convert(geoJson);
  const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
    geometry
  );
  const graphic = new Graphic({
    geometry: {
      ...webMercatorGeometry,
      type: 'polygon'
    },
    symbol: MapSettings.polygonSymbol
  });

  const centroid = graphic.geometry?.centroid;
  if (!centroid || centroid.latitude == null || centroid.longitude == null) {
    return null;
  }

  return {
    latitude: centroid.latitude,
    longitude: centroid.longitude
  };
};

export const addGraphicFromGeoJson = (
  geoJson: GeoJSON.GeometryObject,
  theme: string
) => {
  const { loadedMapModules } = window;
  const { Graphic, WebMercatorUtils } = loadedMapModules;
  // Use centralized utility to find layer
  const drawingLayer: any = findLayerById('drawing-layer');
  const geometry: any = Terraformer.convert(geoJson);
  const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
    geometry
  );
  const graphic = new Graphic({
    geometry: {
      ...webMercatorGeometry,
      type: 'polygon'
    },
    symbol: MapSettings.polygonSymbol
  });

  if (drawingLayer) {
    return addGraphic(
      { graphic, drawingLayer, graphics: undefined },
      'polygon',
      theme
    );
  }
  return {};
};

export const clearGraphic = (
  drawingLayer: __esri.GraphicsLayer,
  sketchViewModel: SketchView
) => {
  sketchViewModel.delete();
  sketchViewModel.cancel();
  if (drawingLayer) {
    drawingLayer.removeAll();
  }
};
